/**
 * Noticias do Cabal Next: o que a home (NewsSection) e as paginas /news e /news/:slug dividem.
 * Dados = getNews()/getNewsBySlug() da API (os mesmos do site antigo). A noticia nao tem campo de categoria: vem do
 * prefixo do titulo, como o Cabal SEA faz -- "[Evento] ...", "[Patch Note] ...", "[Atualização] ...", "[Promoção] ...".
 * So' noticia cadastrada no painel (sem lista de reserva). Imagem: URL ou "/news-img/<nome>" (upload do painel,
 * gravado no disco do host da API -- shared/utils/newsImg resolve).
 */
import { useEffect, useState } from 'react';
import { getNews, type NewsItem } from '@/shared/services/eventsService';

export type NewsCategory = 'evento' | 'atualizacao' | 'promocao' | 'noticia';

export type NewsCard = {
  slug: string | null;   // /news/<slug>; null = sem pagina de detalhe
  image: string;
  date: string;
  title: string;
  text: string;
  category: NewsCategory;
};

export const NEWS_ASSET = '/assets/next';

export const CATEGORY_LABEL: Record<NewsCategory, string> = {
  evento: 'Evento',
  atualizacao: 'Atualização',
  promocao: 'Promoção',
  noticia: 'Notícia',
};

export const NEWS_TABS: { id: 'todas' | NewsCategory; label: string }[] = [
  { id: 'todas', label: 'Todas' },
  { id: 'evento', label: 'Eventos' },
  { id: 'atualizacao', label: 'Atualizações' },
  { id: 'noticia', label: 'Notícias' },
];

export type NewsTab = (typeof NEWS_TABS)[number]['id'];

// Capa da noticia cadastrada sem imagem (arte do site; nao e' noticia inventada)
export const NEWS_DEFAULT_IMAGE = `${NEWS_ASSET}/hero.webp`;

/** "[Evento] ..." -> evento; sem prefixo conhecido -> noticia */
export function newsCategoryOf(title: string): NewsCategory {
  const tag = /^\s*\[([^\]]+)\]/.exec(title)?.[1]?.toLowerCase() ?? '';
  if (/event/.test(tag)) return 'evento';
  if (/patch|atualiza|update|manuten/.test(tag)) return 'atualizacao';
  if (/promo/.test(tag)) return 'promocao';
  return 'noticia';
}

/** Titulo sem o prefixo "[...]" */
export function newsPlainTitle(title: string) {
  return title.replace(/^\s*\[[^\]]+\]\s*/, '');
}

/** Tira tags + decodifica entities (&amp; -> &, &#128680; -> emoji) */
export function newsStripHtml(s: string) {
  if (!s) return '';
  const t = document.createElement('textarea');
  t.innerHTML = s.replace(/<[^>]+>/g, ' ');
  return t.value.replace(/\s+/g, ' ').trim();
}

export function newsDate(iso?: string | null) {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '';
}

export function toNewsCard(n: NewsItem): NewsCard {
  return {
    slug: n.slug || null,
    image: n.image || NEWS_DEFAULT_IMAGE,
    date: newsDate(n.createdDate),
    title: n.title,
    text: n.subTitle || newsStripHtml(n.html),
    category: newsCategoryOf(n.title),
  };
}

/**
 * Lista de noticias = so' as cadastradas no painel (dono, 2026-10-10: o site le as noticias de verdade, sem
 * lista de reserva). Enquanto a API responde: [] + loading; falhou ou vazia: [] (a tela mostra "nenhuma noticia").
 */
export function useNewsCards() {
  const [cards, setCards] = useState<NewsCard[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    getNews()
      .then((list) => {
        if (alive) setCards(list.map(toNewsCard));
      })
      .catch(() => {
        if (alive) setCards([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  return { cards, loading };
}
